import { useState, useEffect, useMemo, useRef } from 'react';
import useLocationManager from '../../../hooks/useLocationManager';
import { sortArrayByProperty } from '../../../../modules/sortUtility/sortUtils';

const zipCodeRegex = /^(\d{5}(-\d{4})?|[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d)$/;

const escapeRegExp = text => text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');

const formatNumberToMaxDigits = number => Math.round(number);

export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const earthRadius = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
    + Math.cos(lat1 * (Math.PI / 180))
    * Math.cos(lat2 * (Math.PI / 180))
    * Math.sin(dLon / 2)
    * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = earthRadius * c;
  const miles = distance * 0.621371;
  return formatNumberToMaxDigits(miles);
};

const fetchGeoCoordinates = searchText => fetch(
  // eslint-disable-next-line max-len
  `https://api.mapbox.com/geocoding/v5/mapbox.places/${searchText}.json?proximity=ip&access_token=${process.env.MAPBOX_API_TOKEN}`,
)
  .then(resp => resp.json())
  .then(data => ({
    lat: data.features[0].center[1],
    lon: data.features[0].center[0],
  }));

const useFacilityManager = (facilities = [], zipCodeRadius = 50) => {
  const { isLocationEnabled, currentLocation } = useLocationManager(true);
  const isAPICallInProgress = useRef(false);
  const [zipCodeInfo, setZipCodeInfo] = useState();
  const [searchText, setSearchTextValue] = useState('');

  const isZipCodeSearch = useMemo(
    () => zipCodeRegex.test(searchText),
    [searchText],
  );

  useEffect(() => {
    if (isZipCodeSearch) {
      const facilityWithSameZipcode = facilities.find(
        facility => facility.address.postal === searchText,
      );

      if (facilityWithSameZipcode) {
        setZipCodeInfo({
          lat: facilityWithSameZipcode.address.latitude,
          lon: facilityWithSameZipcode.address.longitude,
        });
        return;
      }
      // eslint-disable-next-line max-len
      // No facilities with relevant ZipCode were found and hence we make the API Call to find lat,lon using Mapbox
      if (!isAPICallInProgress.current) {
        isAPICallInProgress.current = true;
        fetchGeoCoordinates(searchText)
          .then(setZipCodeInfo)
          .catch((err) => {
            // eslint-disable-next-line no-console
            console.error('Error in fetching Geo Coordinates From MapBox API', err);
          })
          .finally(() => {
            isAPICallInProgress.current = false;
          });
      }
    } else {
      setZipCodeInfo(undefined);
    }
  }, [isZipCodeSearch]);

  const filteredResults = useMemo(() => {
    const searchMatchResults = [];
    const nearbyResults = [];
    const nearbyZipCodeResults = [];
    const isEmptyString = !searchText;
    facilities.forEach((facility) => {
      if (isLocationEnabled && currentLocation) {
        // if location is enabled then we add to nearby Results
        nearbyResults.push({
          ...facility,
          distance: Math.abs(
            calculateDistance(
              currentLocation.lat,
              currentLocation.lon,
              facility.address.latitude,
              facility.address.longitude,
            ),
          ),
        });
      }
      if (!isEmptyString && isZipCodeSearch) {
        if (zipCodeInfo) {
          // eslint-disable-next-line max-len
          // if Zipcode is searched zipCode information is updated and based on distance we add up to list
          const distanceFromZipCode = Math.abs(
            calculateDistance(
              zipCodeInfo.lat,
              zipCodeInfo.lon,
              facility.address.latitude,
              facility.address.longitude,
            ),
          );
          if (distanceFromZipCode <= zipCodeRadius) {
            nearbyZipCodeResults.push({
              ...facility,
              distance: distanceFromZipCode,
            });
          }
        }
      }
      if (!isEmptyString) {
        // add based on filter conditions
        const escapedSearchText = escapeRegExp(searchText);
        const regex = new RegExp(escapedSearchText, 'i');
        const facilityText = facility.address.fullAddress + facility.displayName;
        const validFacilityForSearch = regex.test(facilityText);
        if (validFacilityForSearch) {
          if (isLocationEnabled && currentLocation) {
            searchMatchResults.push({
              ...facility,
              distance: Math.abs(
                calculateDistance(
                  currentLocation.lat,
                  currentLocation.lon,
                  facility.address.latitude,
                  facility.address.longitude,
                ),
              ),
            });
          } else {
            searchMatchResults.push(facility);
          }
        }
      }
    });

    return {
      searchMatchResults,
      nearbyResults: sortArrayByProperty(
        nearbyResults,
        'distance',
        'ascending',
      ),
      nearbyZipCodeResults: sortArrayByProperty(
        nearbyZipCodeResults,
        'distance',
        'ascending',
      ),
    };
  }, [facilities, searchText, isZipCodeSearch, zipCodeInfo, zipCodeRadius]);

  const setSearchText = (newSearchValue) => {
    setSearchTextValue(newSearchValue);
  };

  return {
    facilitiesNearZipCode: filteredResults.nearbyZipCodeResults,
    facilitiesNearLocation: filteredResults.nearbyResults,
    filteredFacilities: filteredResults.searchMatchResults,
    setSearchText,
    searchText,
    isZipCodeSearch,
    isLocationEnabled,
  };
};

export default useFacilityManager;
